import { Skeleton, Stack, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  ConsoleEventCollectionSelectConnectionFragment,
  ConsoleEventDetailCollectionBoxFragment,
  ConsoleEventDetailFragment,
  ConsoleEventDetailQuery,
  Event,
  EventPreSaleBoxFragment,
  EventStatus,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, HeaderImageArea } from '@/components/layouts'
import { EventSettingCell } from '@/components/organisms'
import { Tabs } from '@/components/organisms/functionals'
import { GpsClientInfo, GpsMapModal } from '@/components/organisms/gps'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, Template } from '@/core/architecture'

import {
  CollectionEventCell,
  ConsoleEventDetail,
  CreateCollectionActionSheetCell,
  CreatePreSaleActionSheetCell,
  PreSalesCell,
  TicketStatusCell,
} from './components'

export * from './fragments'

export type State = {
  artistId: string
  eventId: string
  loading: boolean
  tab: 0 | 1
  changingEventStatus: boolean
  deletingEvent: boolean
  removingCollection: boolean
  event: ConsoleEventDetailFragment | null
  collections: ConsoleEventDetailCollectionBoxFragment[] | null
  isInside: boolean
  gpsClientInfo: Maybe<GpsClientInfo>
  preSale: ConsoleEventDetailQuery['artistPreSales'] | undefined
  hasPresalesNextPage: boolean
  updatedAt?: string
  selectCollection: {
    loading: boolean
    hasNextPage: boolean
    addingCollection: boolean
    collections: Maybe<ConsoleEventCollectionSelectConnectionFragment>
  }
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onRequestPermission: () => Promise<void>
  onFetchPreSalesMore: () => Promise<void>
  onTogglePreSalePublish: (preSale: EventPreSaleBoxFragment) => Promise<void>
  onFetchSelectCollection: () => Promise<void>
  onFetchMoreSelectCollection: () => Promise<void>
  onAddCollectionsToEvent: (collectionIds: string[]) => Promise<void>
  onRemoveCollectionFromEvent: (collectionId: string) => Promise<void>
  onChangeEventStatus: (status: EventStatus) => Promise<void>
  onDeleteEvent: () => Promise<void>
}

export const ConsoleEventDetailTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onRequestPermission,
  onFetchPreSalesMore,
  onTogglePreSalePublish,
  onFetchSelectCollection,
  onFetchMoreSelectCollection,
  onAddCollectionsToEvent,
  onRemoveCollectionFromEvent,
  onChangeEventStatus,
  onDeleteEvent,
}) => {
  const [openMap, setOpenMap] = useState<boolean>(false)
  const showSkeleton = state.loading && !state.event
  const [currentTab, setCurrentTab] = useState<number>(state.tab)
  const { t } = useTranslation('consoleEventDetail')
  const { routerPages, routerNames, generatePath } = useRouter()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleTabChange = useCallback(
    (value: number) => {
      setCurrentTab(value)
      // If you use router.replace, the page will re-render, causing it to scroll to the top.
      // https://stackoverflow.com/a/61596862
      window.history.replaceState(
        window.history.state, // keep current state
        state.event?.title ?? '',
        generatePath(routerPages.ConsoleShokufesEventDetail, {
          eventId: state.eventId,
          tab: value === 1 ? 'store' : 'presale',
        })
      )
    },
    [
      generatePath,
      routerPages.ConsoleShokufesEventDetail,
      state.event?.title,
      state.eventId,
    ]
  )

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <BaseLayout
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleShokufesEvents,
          title: state.event?.title,
          right: (
            <IconButton.More
              size="medium"
              color="white"
              edge="end"
              onClick={event => setAnchorEl(event.currentTarget)}
            />
          ),
        }}
      >
        <Head title={routerNames.ConsoleShokufesEventDetail} />
        <EventSettingCell
          eventId={state.eventId}
          changingEventStatus={state.changingEventStatus}
          deletingEvent={state.deletingEvent}
          anchorEl={anchorEl}
          status={state.event?.status}
          onClose={() => setAnchorEl(null)}
          onChangeEventStatus={onChangeEventStatus}
          onDeleteEvent={onDeleteEvent}
        />
        <ConsoleEventImageCell
          loading={showSkeleton}
          thumbnail={state.event?.thumbnail}
        />
        <ConsoleEventDetail
          loading={showSkeleton}
          event={state.event}
          onOpenMap={async () => {
            setOpenMap(true)
            await onRequestPermission()
          }}
        />
        {config.modules.preSale ? (
          <>
            {state.event && (
              <Stack px={5}>
                <TicketStatusCell
                  preSalesAggregation={state.event.preSalesAggregation ?? null}
                  updatedAt={state.updatedAt}
                  showEmptyState
                />
              </Stack>
            )}
            <Typography variant="h4" px={5} pb={2} lineHeight="28px">
              {t('salesManagement', '販売管理')}
            </Typography>
            <Tabs
              labels={[t('presales'), t('store')]}
              value={currentTab}
              disableAnimateTransitions
              onChange={handleTabChange}
            >
              <PreSalesCell
                loading={state.loading}
                preSales={state.preSale}
                hasNextPage={state.hasPresalesNextPage}
                onFetchMore={onFetchPreSalesMore}
                onTogglePreSalePublish={onTogglePreSalePublish}
              />
              <CollectionEventCell
                loading={state.loading}
                removingCollection={state.removingCollection}
                collections={state.collections}
                onFetchMore={onFetchMore}
                eventId={state.event?.id ?? ''}
                onRemoveCollectionFromEvent={onRemoveCollectionFromEvent}
              />
            </Tabs>
            <CreatePreSaleActionSheetCell isShow={currentTab === 0} />
            <CreateCollectionActionSheetCell
              artistId={state.artistId}
              isShow={currentTab === 1}
              {...state.selectCollection}
              onFetchSelectCollection={onFetchSelectCollection}
              onFetchMoreSelectCollection={onFetchMoreSelectCollection}
              onAddCollectionsToEvent={onAddCollectionsToEvent}
            />
          </>
        ) : (
          <>
            <CollectionEventCell
              loading={state.loading}
              removingCollection={state.removingCollection}
              collections={state.collections}
              onFetchMore={onFetchMore}
              eventId={state.event?.id ?? ''}
              onRemoveCollectionFromEvent={onRemoveCollectionFromEvent}
            />
            <CreateCollectionActionSheetCell
              isShow
              artistId={state.artistId}
              {...state.selectCollection}
              onFetchSelectCollection={onFetchSelectCollection}
              onFetchMoreSelectCollection={onFetchMoreSelectCollection}
              onAddCollectionsToEvent={onAddCollectionsToEvent}
            />
          </>
        )}
        <GpsMapModal
          title={state.event?.location?.approximateAddress ?? ''}
          open={openMap}
          onClose={() => setOpenMap(false)}
          targetData={state.event?.location}
          currentData={state.gpsClientInfo?.currentLocation}
          isInside={state.isInside}
          hideAnnotation
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type ConsoleEventImageCellProps = {
  loading: boolean
  thumbnail?: Event['thumbnail']
}
const ConsoleEventImageCell: Cell<ConsoleEventImageCellProps> = ({
  loading,
  thumbnail,
}) => {
  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={212} />
  }
  return <HeaderImageArea imageUri={thumbnail?.mediumUri} />
}
